import dayjs, { Dayjs } from 'dayjs'
import { TypeSignUpApplicationResponse } from '../Hooks'

export const isVerificationSuccessful = (
  verification: TypeSignUpApplicationResponse | undefined
) => {
  if (!verification) {
    return false
  }

  return (
    !verification.duplicateExists &&
    !verification.potentialSanctionedMatch &&
    !verification.potentialFraudMatch &&
    verification.isVerified
  )
}

const NOT_NUMBERS_AND_SLASHES = /[^\d/]/g
const MAX_AGE = 150

export function validateDateOfBirth(dateOfBirth: string, returnTheDateIfValid = false): string {
  if (!dateOfBirth) {
    return ''
  }

  dateOfBirth = dateOfBirth.replace(NOT_NUMBERS_AND_SLASHES, '')
  if (
    dateOfBirth.length < 10 ||
    dateOfBirth.indexOf('/') < 0 ||
    dateOfBirth.lastIndexOf('/') === dateOfBirth.indexOf('/')
  ) {
    return 'Please enter a valid date of birth.'
  }

  let eighteenYearsAgo: Date | Dayjs = new Date()
  eighteenYearsAgo.setFullYear(eighteenYearsAgo.getFullYear() - 18)
  eighteenYearsAgo.setHours(0)
  eighteenYearsAgo.setMinutes(0)
  eighteenYearsAgo.setSeconds(0)
  eighteenYearsAgo.setMilliseconds(0)
  eighteenYearsAgo = dayjs(eighteenYearsAgo)

  const birthYear = Number(dateOfBirth.substring(dateOfBirth.lastIndexOf('/') + 1))
  const birthDay = Number(dateOfBirth.substring(0, dateOfBirth.indexOf('/')))

  const birthMonth = Number(
    dateOfBirth.substring(dateOfBirth.indexOf('/') + 1, dateOfBirth.lastIndexOf('/'))
  )

  if (birthDay > 31 || birthDay < 1) {
    return 'Please enter a day of the month between 1 and 31.'
  }

  if (birthMonth > 12 || birthMonth < 1) {
    return 'Please enter a month between 1 and 12.'
  }

  const currentYear = dayjs().year()
  if (birthYear > currentYear || birthYear < currentYear - MAX_AGE) {
    return 'Please enter a valid date of birth.'
  }

  const birthDate = dayjs(new Date(birthYear, birthMonth - 1, birthDay))
  if (birthDate.date() !== birthDay) {
    // This can happen when invalid days are entered. E.g. 30/2/1996 or 31/11/2000.
    return 'Please enter a valid date.'
  }

  const is18 = birthDate.isSame(eighteenYearsAgo) || birthDate.isBefore(eighteenYearsAgo)
  if (!is18) {
    return 'You must be aged 18 or over to wager in Australia.'
  } else {
    if (returnTheDateIfValid) {
      return birthDate.format('YYYY-MM-DD')
    }

    return ''
  }
}

export function getFormattedDateOfBirth(
  enteredText: string,
  oldText: string,
  selectionStart: number | null
) {
  const numbersAndSlashesOnly = enteredText.replace(NOT_NUMBERS_AND_SLASHES, '')
  const digits = numbersAndSlashesOnly.replace(/[^0-9]/g, '')

  // Once all 8 digits are entered, always format fully
  if (digits.length === 8) {
    return `${digits.substring(0, 2)}/${digits.substring(2, 4)}/${digits.substring(4)}`
  }

  // Ignore if user enters consecutive slashes
  if (numbersAndSlashesOnly.includes('//')) {
    return oldText
  }

  // The user has deleted/replaced something, so just let them do it
  if (enteredText.length <= oldText.length && enteredText !== oldText) {
    return enteredText
  }

  // The user is typing in the middle of the string, so just let them do it
  if ((selectionStart || oldText.length) < oldText.length) {
    return enteredText
  }

  let result = numbersAndSlashesOnly
  const slashCount = (result.match(/\//g) || []).length

  // Auto-add slash after 2 digits (day)
  if (slashCount === 0 && digits.length === 2) {
    result = result + '/'
  }

  // Auto-add slash after 4 digits (month)
  if (slashCount === 1 && digits.length === 4) {
    result = result + '/'
  }

  if (result.indexOf('/') === 1) {
    result = '0' + result
  }

  if (result.lastIndexOf('/') === 4) {
    result = `${result.substring(0, 3)}0${result.substring(3)}`
  }

  return result
}
